import React, { useState, useEffect } from 'react';
import { tradingAPI } from '../../../services/tradingAPI';
import '../../../css/trading.css';

const Orders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    fetchOrders();
  }, [statusFilter]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const params = statusFilter !== 'all' ? { status: statusFilter } : {};
      const response = await tradingAPI.getOrders(params);
      setOrders(response.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load orders');
      console.error('Orders error:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    if (!value) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(value);
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getOrderTypeLabel = (orderType) => {
    const labels = {
      market: 'Market',
      limit: 'Limit',
      stop: 'Stop',
      stop_limit: 'Stop Limit',
    };
    return labels[orderType] || orderType;
  };

  const getStatusBadgeClass = (status) => {
    const classes = {
      pending: 'badge-warning',
      filled: 'badge-success',
      partially_filled: 'badge-info',
      canceled: 'badge-secondary',
      rejected: 'badge-error',
      expired: 'badge-secondary',
    };
    return classes[status] || 'badge-secondary';
  };

  if (loading) {
    return (
      <div className="trading-container">
        <div className="loading">Loading orders...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="trading-container">
        <div className="error-message">
          <h3>Error Loading Orders</h3>
          <p>{error}</p>
          <button onClick={fetchOrders} className="btn btn-primary">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="trading-container">
      <div className="trading-header">
        <h1>Order History</h1>
        <div className="header-actions">
          <button onClick={fetchOrders} className="btn btn-secondary">
            Refresh
          </button>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="filter-tabs">
        <button
          className={`tab ${statusFilter === 'all' ? 'active' : ''}`}
          onClick={() => setStatusFilter('all')}
        >
          All Orders
        </button>
        <button
          className={`tab ${statusFilter === 'pending' ? 'active' : ''}`}
          onClick={() => setStatusFilter('pending')}
        >
          Pending
        </button>
        <button
          className={`tab ${statusFilter === 'filled' ? 'active' : ''}`}
          onClick={() => setStatusFilter('filled')}
        >
          Filled
        </button>
        <button
          className={`tab ${statusFilter === 'canceled' ? 'active' : ''}`}
          onClick={() => setStatusFilter('canceled')}
        >
          Canceled
        </button>
      </div>

      {/* Orders Table */}
      <div className="orders-section">
        {orders && orders.length > 0 ? (
          <div className="orders-table">
            <table>
              <thead>
                <tr>
                  <th>Date/Time</th>
                  <th>Symbol</th>
                  <th>Side</th>
                  <th>Type</th>
                  <th>Qty</th>
                  <th>Limit Price</th>
                  <th>Filled Price</th>
                  <th>Filled Qty</th>
                  <th>Status</th>
                  <th>Order ID</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => (
                  <tr key={order.id}>
                    <td>{formatDateTime(order.created_at)}</td>
                    <td className="symbol">{order.symbol}</td>
                    <td>
                      <span className={`badge badge-${order.side}`}>
                        {order.side.toUpperCase()}
                      </span>
                    </td>
                    <td>{getOrderTypeLabel(order.order_type)}</td>
                    <td>{order.qty}</td>
                    <td>{formatCurrency(order.limit_price)}</td>
                    <td>{formatCurrency(order.filled_price)}</td>
                    <td>{order.filled_qty || 0}</td>
                    <td>
                      <span className={`badge ${getStatusBadgeClass(order.status)}`}>
                        {order.status}
                      </span>
                    </td>
                    <td className="order-id">{order.alpaca_order_id || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="empty-state">
            <h3>No Orders Found</h3>
            <p>
              {statusFilter === 'pending'
                ? 'No pending orders'
                : statusFilter === 'filled'
                ? 'No filled orders'
                : statusFilter === 'canceled'
                ? 'No canceled orders'
                : 'No orders to display'}
            </p>
          </div>
        )}
      </div>

      {/* Order Stats */}
      {orders && orders.length > 0 && (
        <div className="order-stats">
          <div className="stats-card">
            <div className="stat-label">Total Orders</div>
            <div className="stat-value">{orders.length}</div>
          </div>
          <div className="stats-card">
            <div className="stat-label">Filled</div>
            <div className="stat-value">
              {orders.filter((o) => o.status === 'filled').length}
            </div>
          </div>
          <div className="stats-card">
            <div className="stat-label">Pending</div>
            <div className="stat-value">
              {orders.filter((o) => o.status === 'pending').length}
            </div>
          </div>
          <div className="stats-card">
            <div className="stat-label">Canceled</div>
            <div className="stat-value">
              {orders.filter((o) => o.status === 'canceled').length}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Orders;
